import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import styles from './Navbar.module.css';
import {
  QrCode, Camera, CreditCard, Smartphone, Package,
  Wallet, Wifi, MessageCircle, Phone, Banknote,
  ScanLine, FileImage, IndianRupee, BookOpen,
  HelpCircle, Users2, Mail, Sun, Moon, Menu, X,
  ChevronDown as ChevronDownIcon,
} from 'lucide-react';

const ChevronDown = () => <ChevronDownIcon size={12} strokeWidth={2.5} />;

const openGenerator = () => {
  setTimeout(() => {
    const el = document.getElementById('tool');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    window.dispatchEvent(new CustomEvent('openGenerator'));
  }, 100);
};

const openScanner = () => {
  setTimeout(() => {
    const el = document.getElementById('tool');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    window.dispatchEvent(new CustomEvent('openScanner'));
  }, 100);
};

const NAV_STRUCTURE = [
  {
    label: 'Tools',
    dropdown: [
      { to: '/', icon: QrCode,      label: 'QR Generator',  desc: 'Create any QR code free',        action: 'generator' },
      { to: '/', icon: Camera,      label: 'QR Scanner',    desc: 'Scan via camera or image',        action: 'scanner'   },
      { to: '/qr-card-creator',     icon: CreditCard,   label: 'Business Card',  desc: 'Beautiful QR cards',              badge: 'NEW' },
      { to: '/story-creator',       icon: Smartphone,   label: 'Story Creator',  desc: 'WhatsApp & Instagram story',      badge: 'NEW' },
      { to: '/bulk-qr-generator',   icon: Package,      label: 'Bulk Generator', desc: 'Generate QR codes in bulk' },
    ]
  },
  {
    label: 'QR Types',
    dropdown: [
      { to: '/upi-qr-code-generator',           icon: Wallet,         label: 'UPI Payment',      desc: 'PhonePe, GPay, Paytm'       },
      { to: '/wifi-qr-code-generator',           icon: Wifi,           label: 'WiFi',             desc: 'Share password instantly'   },
      { to: '/whatsapp-qr-code-generator',       icon: MessageCircle,  label: 'WhatsApp',         desc: 'Let customers message you'  },
      { to: '/qr-code-for-phone-number',         icon: Phone,          label: 'Phone Number',     desc: 'One scan to call'           },
      { to: '/qr-code-for-payment',              icon: Banknote,       label: 'Payment',          desc: 'Accept digital payments'    },
      { to: '/qr-code-scanner-online',           icon: ScanLine,       label: 'Scanner Online',   desc: 'No app needed'              },
      { to: '/scan-qr-code-from-image',          icon: FileImage,      label: 'Scan from Image',  desc: 'Upload to decode'           },
      { to: '/google-pay-qr-code-generator',     icon: IndianRupee,    label: 'Google Pay',       desc: 'GPay QR generator'          },
      { to: '/free-qr-code-generator-india',     icon: QrCode,         label: 'India Free QR',    desc: 'Made for India'             },
    ]
  },
  {
    label: 'Learn',
    dropdown: [
      { to: '/blog',          icon: BookOpen,   label: 'Blog',         desc: 'Guides and tutorials'  },
      { to: '/how-it-works',  icon: HelpCircle, label: 'How it Works', desc: 'Learn about QR codes'  },
    ]
  },
  {
    label: 'Company',
    dropdown: [
      { to: '/about',   icon: Users2, label: 'About',   desc: 'Our story'    },
      { to: '/contact', icon: Mail,   label: 'Contact',  desc: 'Get in touch' },
    ]
  },
];

function DropdownMenu({ items, onClose }) {
  return (
    <div className={styles.dropdown}>
      {items.map(item => {
        const Icon = item.icon;
        return (
          <Link
            key={item.label}
            to={item.to}
            className={styles.dropdownItem}
            onClick={() => {
              onClose();
              if (item.action === 'generator') openGenerator();
              if (item.action === 'scanner') openScanner();
            }}
          >
            <div className={styles.dropdownLabel}>
              {Icon && <span className={styles.navIcon}><Icon size={14} strokeWidth={2} /></span>}
              {item.label}
              {item.badge && <span className={styles.badge}>{item.badge}</span>}
            </div>
            <div className={styles.dropdownDesc}>{item.desc}</div>
          </Link>
        );
      })}
    </div>
  );
}

export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setMenuOpen(false);
  }, [location]);

  return (
    <nav className={styles.nav} ref={navRef}>
      <div className={`container ${styles.inner}`}>

        {/* Logo */}
        <Link to="/" className={styles.logo} onClick={() => { openGenerator(); setOpenDropdown(null); }}>
          <img src="/favicon.svg" className={styles.logoIcon} width="32" height="32" alt="SimplifyQR" />
          simplifyqr
        </Link>

        {/* Desktop nav */}
        <div className={styles.links}>
          {NAV_STRUCTURE.map(group => (
            <div
              key={group.label}
              className={styles.navGroup}
              onMouseEnter={() => setOpenDropdown(group.label)}
              onMouseLeave={() => {
                setTimeout(() => {
                  setOpenDropdown(prev => prev === group.label ? null : prev);
                }, 150);
              }}
            >
              <button
                className={`${styles.navGroupBtn} ${openDropdown === group.label ? styles.navGroupBtnActive : ''}`}
                onClick={() => setOpenDropdown(openDropdown === group.label ? null : group.label)}
              >
                {group.label}
                <span className={`${styles.chevron} ${openDropdown === group.label ? styles.chevronOpen : ''}`}>
                  <ChevronDown />
                </span>
              </button>
              {openDropdown === group.label && (
                <DropdownMenu items={group.dropdown} onClose={() => setOpenDropdown(null)} />
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <Link to="/qr-card-creator" className={styles.ctaBtn}>
            <CreditCard size={14} strokeWidth={2} />
            Card Creator
          </Link>
          <button className={styles.themeBtn} onClick={toggleTheme} title="Toggle theme">
            {theme === 'dark' ? <Sun size={16} strokeWidth={2} /> : <Moon size={16} strokeWidth={2} />}
          </button>
          <button className={styles.hamburger} onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
            {menuOpen ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className={styles.mobileMenu}>
          {NAV_STRUCTURE.map(group => (
            <div key={group.label} className={styles.mobileGroup}>
              <button
                className={styles.mobileGroupBtn}
                onClick={() => setMobileExpanded(mobileExpanded === group.label ? null : group.label)}
              >
                {group.label}
                <span className={`${styles.chevron} ${mobileExpanded === group.label ? styles.chevronOpen : ''}`}>
                  <ChevronDown />
                </span>
              </button>
              {mobileExpanded === group.label && (
                <div className={styles.mobileDropdown}>
                  {group.dropdown.map(item => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.label}
                        to={item.to}
                        className={styles.mobileLink}
                        onClick={() => {
                          setMenuOpen(false);
                          setMobileExpanded(null);
                          if (item.action === 'generator') openGenerator();
                          if (item.action === 'scanner') openScanner();
                        }}
                      >
                        {Icon && <span className={styles.navIcon}><Icon size={14} strokeWidth={2} /></span>}
                        {item.label}
                        {item.badge && <span className={styles.badge}>{item.badge}</span>}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}
